import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { getDirtyFields, required } from '@ubeya/shared/utils/form';
import { ConfirmationModal, FormModal, ImageField, InputField, TextAreaField } from '@ubeya/shared-web/components';
import { responsiveFields } from '@ubeya/shared-web/style';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';

const Container = styled.div`
  ${responsiveFields.mobile};
  .full:first-child {
    max-width: 150px;
    margin: 10px auto 0;
  }
`;

const UniformModal = ({ selectedUniform, onClose, onConfirm }) => {
  const { t } = useContext(ConfigContext);
  const [showPromptBeforeUpdate, setShowPromptBeforeUpdate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const initialValues = useMemo(() => ({ name: undefined, description: '', image: '', ...selectedUniform }), [
    selectedUniform
  ]);

  const submitUniform = useCallback(
    async (values, form) => {
      setIsLoading(true);
      const { dirtyFields } = form.getState();

      const dirtyValues = getDirtyFields(values, dirtyFields);
      await onConfirm({ id: values.id, name: values?.name, ...dirtyValues });

      setIsLoading(false);
      onClose();
    },
    [onClose, onConfirm]
  );

  const handleSubmit = (values, form) => {
    const { dirtyFields } = form.getState();

    if (selectedUniform?.id && Object.entries(dirtyFields)?.length > 0) {
      setShowPromptBeforeUpdate({ values, form });
    } else {
      submitUniform(values, form);
    }
  };

  return (
    <FormModal
      initialValues={initialValues}
      onSubmit={handleSubmit}
      size="long"
      onClose={onClose}
      loadingConfirm={isLoading}
      title={selectedUniform.id ? t('editUniform') : t('addUniform')}
      confirmText={selectedUniform.id ? t('update') : t('add')}
      showCancelButton
      disabledOnInvalid
      disabledOnPristine={false}>
      <Container>
        <ImageField name="image" className="full" cropBeforeUpload={false} />
        <InputField name="name" title={t('name')} className="full" autoFocus validate={required} required />

        <TextAreaField
          name="description"
          title={t('description')}
          className="full"
          placeholder={t('addDescription')}
          minRows={2}
          maxLength={2000}
          validate={required}
          showCharsCount
          required
        />
      </Container>

      {showPromptBeforeUpdate && (
        <ConfirmationModal
          onClose={() => setShowPromptBeforeUpdate(false)}
          title={t('areYouSure')}
          body={t('areYouSureUpdateUniform')}
          onConfirm={() => {
            submitUniform(showPromptBeforeUpdate.values, showPromptBeforeUpdate.form);
            setShowPromptBeforeUpdate(false);
          }}
          confirmText={t('ok')}
          showCancelButton
        />
      )}
    </FormModal>
  );
};

export default UniformModal;
